import React, { useState, useEffect } from 'react';
import { Zap } from 'lucide-react';
import { FinancialValue } from './shared/FinancialValue';

interface PLData {
  by_year: {
    [year: string]: {
      revenue: number;
      cogs: number;
      payroll: number;
      expenses: number;
    };
  };
  totals: {
    total_revenue: number;
    total_cogs: number;
    total_payroll: number;
    total_expenses: number;
  };
}

interface EditablePLTableProps {
  data: PLData;
  assumptions: any[];
  onChange: (updatedPL: PLData, userOverrides: any) => void;
}

interface EditingCell {
  year: string;
  field: string;
}

export default function EditablePLTable({ data, assumptions, onChange }: EditablePLTableProps) {
  const [plData, setPLData] = useState<PLData>(data);
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [userOverrides, setUserOverrides] = useState<Record<string, Record<string, number>>>({});
  const [showCOGSHelper, setShowCOGSHelper] = useState(false);
  const [cogsPercentage, setCOGSPercentage] = useState(35);

  // Sync with parent data
  useEffect(() => {
    setPLData(data);
  }, [data]);

  const years = Object.keys(plData.by_year || {}).sort();

  const handleCellClick = (year: string, field: string, currentValue: number) => {
    setEditingCell({ year, field });
    setEditValue(currentValue.toString());
  };

  const handleCellBlur = () => {
    if (!editingCell) return;

    const newValue = parseFloat(editValue) || 0;
    const { year, field } = editingCell;

    // Update the data
    const updatedPL = { ...plData };
    updatedPL.by_year = { ...updatedPL.by_year };
    updatedPL.by_year[year] = { ...updatedPL.by_year[year], [field]: newValue };

    // Recalculate totals
    updatedPL.totals = {
      total_revenue: Object.values(updatedPL.by_year).reduce((sum, y) => sum + (y.revenue || 0), 0),
      total_cogs: Object.values(updatedPL.by_year).reduce((sum, y) => sum + (y.cogs || 0), 0),
      total_payroll: Object.values(updatedPL.by_year).reduce((sum, y) => sum + (y.payroll || 0), 0),
      total_expenses: Object.values(updatedPL.by_year).reduce((sum, y) => sum + (y.expenses || 0), 0),
    };

    // Track user override
    const newOverrides = { ...userOverrides };
    if (!newOverrides[year]) newOverrides[year] = {};
    newOverrides[year][field] = newValue;
    setUserOverrides(newOverrides);

    setPLData(updatedPL);
    onChange(updatedPL, newOverrides);
    setEditingCell(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCellBlur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const applyCOGSPercentage = () => {
    const updatedPL = { ...plData };
    updatedPL.by_year = { ...updatedPL.by_year };
    
    const newOverrides = { ...userOverrides };

    years.forEach(year => {
      const revenue = updatedPL.by_year[year].revenue || 0;
      const newCOGS = Math.round(revenue * (cogsPercentage / 100));
      updatedPL.by_year[year] = { ...updatedPL.by_year[year], cogs: newCOGS };
      
      if (!newOverrides[year]) newOverrides[year] = {};
      newOverrides[year].cogs = newCOGS;
    });

    // Recalculate totals
    updatedPL.totals = {
      ...updatedPL.totals,
      total_cogs: Object.values(updatedPL.by_year).reduce((sum, y) => sum + (y.cogs || 0), 0),
    };

    setPLData(updatedPL);
    setUserOverrides(newOverrides);
    onChange(updatedPL, newOverrides);
    setShowCOGSHelper(false);
  };

  const resetToEstimated = () => {
    setPLData(data);
    setUserOverrides({});
    onChange(data, {});
  };

  const isOverridden = (year: string, field: string) => {
    return userOverrides[year]?.[field] !== undefined;
  };

  const renderEditableCell = (year: string, field: string, value: number, bgClass: string = '') => {
    const isEditing = editingCell?.year === year && editingCell?.field === field;
    const wasOverridden = isOverridden(year, field);

    if (isEditing) {
      return (
        <td key={`${year}-${field}`} className={`px-2 py-1 ${bgClass}`}>
          <input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleCellBlur}
            onKeyDown={handleKeyDown}
            autoFocus
            className="w-full px-2 py-1 text-right border-2 rounded focus:outline-none text-sm"
            style={{ borderColor: 'var(--color-brand)' }}
          />
        </td>
      );
    }

    return (
      <td
        key={`${year}-${field}`}
        onClick={() => handleCellClick(year, field, value)}
        className={`text-right px-4 py-2 cursor-pointer transition-colors ${bgClass} ${
          wasOverridden ? 'ring-2 ring-inset' : ''
        }`}
        style={wasOverridden ? { '--tw-ring-color': 'var(--color-needs-human-tint-bd)' } as React.CSSProperties : undefined}
        title={wasOverridden ? 'User override (click to edit)' : 'Click to edit'}
      >
        <span className="flex items-center justify-end gap-1">
          {wasOverridden && <span className="text-xs" style={{ color: 'var(--color-needs-human-text)' }}>✎</span>}
          <FinancialValue value={value} />
        </span>
      </td>
    );
  };

  const grossProfit = (plData.totals?.total_revenue || 0) - (plData.totals?.total_cogs || 0);
  const hasOverrides = Object.keys(userOverrides).length > 0;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Click any value to edit</span>
          {hasOverrides && (
            <span className="px-2 py-1 text-xs rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
              {Object.values(userOverrides).reduce((count, year) => count + Object.keys(year).length, 0)} overrides
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCOGSHelper(!showCOGSHelper)}
            className="px-3 py-1 text-sm rounded-lg transition-colors inline-flex items-center gap-1"
            style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
          >
            <Zap size={14} /> Adjust COGS %
          </button>
          {hasOverrides && (
            <button
              onClick={resetToEstimated}
              className="px-3 py-1 text-sm rounded-lg transition-colors"
              style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            >
              ↺ Reset All
            </button>
          )}
        </div>
      </div>

      {/* COGS Helper */}
      {showCOGSHelper && (
        <div className="p-4 border rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <h4 className="font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>Adjust COGS as % of Revenue</h4>
          <p className="text-sm mb-3" style={{ color: 'var(--color-needs-human-text)' }}>
            Apply a uniform COGS percentage across all years. Industry defaults:
            <span className="block mt-1">
              • Restaurants: 28-35% • Retail: 40-60% • Services: 15-25%
            </span>
          </p>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="10"
              max="70"
              value={cogsPercentage}
              onChange={(e) => setCOGSPercentage(Number(e.target.value))}
              className="flex-1"
            />
            <input
              type="number"
              value={cogsPercentage}
              onChange={(e) => setCOGSPercentage(Number(e.target.value))}
              className="w-16 px-2 py-1 border rounded text-center"
              style={{ borderColor: 'var(--color-border)' }}
            />
            <span style={{ color: 'var(--color-needs-human-text)' }}>%</span>
            <button
              onClick={applyCOGSPercentage}
              className="px-4 py-1 rounded-lg text-sm"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg" style={{ borderColor: 'var(--color-border)' }}>
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <tr>
              <th className="text-left px-4 py-3 font-semibold w-40" style={{ color: 'var(--color-text-secondary)' }}>Category</th>
              {years.map(year => (
                <th key={year} className="text-right px-4 py-3 font-semibold min-w-[120px]" style={{ color: 'var(--color-text-secondary)' }}>
                  {year}
                </th>
              ))}
              <th className="text-right px-4 py-3 font-semibold min-w-[120px]" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-hover)' }}>Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--color-border-faint)]">
            {/* Revenue */}
            <tr style={{ backgroundColor: 'transparent' }}>
              <td className="px-4 py-2 font-medium" style={{ color: 'var(--color-verified)' }}>
                Revenue
                <span className="block text-xs font-normal" style={{ color: 'var(--color-verified)' }}>From deposits</span>
              </td>
              {years.map(year => renderEditableCell(year, 'revenue', plData.by_year[year]?.revenue || 0, ''))}
              <td className="text-right px-4 py-2 font-bold" style={{ color: 'var(--color-verified)', backgroundColor: 'transparent' }}>
                <FinancialValue value={plData.totals?.total_revenue} />
              </td>
            </tr>

            {/* COGS */}
            <tr style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
              <td className="px-4 py-2 font-medium" style={{ color: 'var(--color-needs-human-text)' }}>
                COGS
                <span className="block text-xs font-normal" style={{ color: 'var(--color-needs-human-text)' }}>
                  {assumptions?.find(a => a.field === 'cogs') ? (
                    <span className="inline-flex items-center gap-1"><Zap size={12} /> Estimated</span>
                  ) : 'Direct'}
                </span>
              </td>
              {years.map(year => renderEditableCell(year, 'cogs', plData.by_year[year]?.cogs || 0, ''))}
              <td className="text-right px-4 py-2 font-bold" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' }}>
                <FinancialValue value={plData.totals?.total_cogs} />
              </td>
            </tr>

            {/* Payroll */}
            <tr>
              <td className="px-4 py-2 font-medium" style={{ color: 'var(--color-text-primary)' }}>
                Payroll
                <span className="block text-xs font-normal" style={{ color: 'var(--color-text-muted)' }}>Labor costs</span>
              </td>
              {years.map(year => renderEditableCell(year, 'payroll', plData.by_year[year]?.payroll || 0))}
              <td className="text-right px-4 py-2 font-bold" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-elevated)' }}>
                <FinancialValue value={plData.totals?.total_payroll} />
              </td>
            </tr>

            {/* Other Expenses */}
            <tr>
              <td className="px-4 py-2 font-medium" style={{ color: 'var(--color-text-primary)' }}>
                Other Expenses
                <span className="block text-xs font-normal" style={{ color: 'var(--color-text-muted)' }}>Rent, utilities, etc.</span>
              </td>
              {years.map(year => renderEditableCell(year, 'expenses', plData.by_year[year]?.expenses || 0))}
              <td className="text-right px-4 py-2 font-bold" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-elevated)' }}>
                <FinancialValue value={plData.totals?.total_expenses} />
              </td>
            </tr>

            {/* Gross Profit (calculated) */}
            <tr className="font-bold" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
              <td className="px-4 py-3" style={{ color: 'var(--color-needs-human-text)' }}>
                Gross Profit
                <span className="block text-xs font-normal">Revenue - COGS</span>
              </td>
              {years.map(year => {
                const yearGP = (plData.by_year[year]?.revenue || 0) - (plData.by_year[year]?.cogs || 0);
                return (
                  <td key={year} className="text-right px-4 py-3" style={{ color: 'var(--color-needs-human-text)' }}>
                    <FinancialValue value={yearGP} />
                  </td>
                );
              })}
              <td className="text-right px-4 py-3" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint-bd)' }}>
                <FinancialValue value={grossProfit} />
              </td>
            </tr>

            {/* Net Operating Income (calculated) */}
            <tr className="font-bold" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
              <td className="px-4 py-3" style={{ color: 'var(--color-needs-human-text)' }}>
                Net Operating Income
                <span className="block text-xs font-normal">GP - Payroll - Expenses</span>
              </td>
              {years.map(year => {
                const yearNOI = (plData.by_year[year]?.revenue || 0)
                  - (plData.by_year[year]?.cogs || 0)
                  - (plData.by_year[year]?.payroll || 0)
                  - (plData.by_year[year]?.expenses || 0);
                return (
                  <td key={year} className="text-right px-4 py-3" style={{ color: 'var(--color-needs-human-text)' }}>
                    <FinancialValue value={yearNOI} />
                  </td>
                );
              })}
              <td className="text-right px-4 py-3" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint-bd)' }}>
                <FinancialValue value={
                  (plData.totals?.total_revenue || 0) 
                  - (plData.totals?.total_cogs || 0)
                  - (plData.totals?.total_payroll || 0)
                  - (plData.totals?.total_expenses || 0)
                } />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Override Summary */}
      {hasOverrides && (
        <div className="p-3 border rounded-lg text-sm" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <h4 className="font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>Your Overrides</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(userOverrides).map(([year, fields]) =>
              Object.entries(fields).map(([field, value]) => (
                <span
                  key={`${year}-${field}`}
                  className="px-2 py-1 rounded text-xs"
                  style={{ backgroundColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
                >
                  {year} {field}: <FinancialValue value={value as number} />
                </span>
              ))
            )}
          </div>
          <p className="text-xs mt-2" style={{ color: 'var(--color-needs-human-text)' }}>
            These overrides will be tracked in the audit trail for court defensibility.
          </p>
        </div>
      )}
    </div>
  );
}
